import { Mail, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OutboundEmailStatus } from '~/graphql/graphql';
import { openEmailPreview } from '~/pages/messages/openEmailPreview';
import type { NotificationSentMessage } from '~/pages/messages/Messages';

const STATE_STYLES: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]: 'text-amber-700',
  [OutboundEmailStatus.Sent]: 'text-green-700',
  [OutboundEmailStatus.Failed]: 'text-red-700',
};

const STATE_KEYS: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]: 'messages.notification_sent.state_pending',
  [OutboundEmailStatus.Sent]: 'messages.notification_sent.state_sent',
  [OutboundEmailStatus.Failed]: 'messages.notification_sent.state_failed',
};

const KIND_KEYS: Record<
  NotificationSentMessage['kind'],
  { titleKey: string; descriptionKey: string }
> = {
  logistics: {
    titleKey: 'messages.notification_sent.title_logistics',
    descriptionKey: 'messages.notification_sent.description_logistics',
  },
  vendor: {
    titleKey: 'messages.notification_sent.title_vendor',
    descriptionKey: 'messages.notification_sent.description_vendor',
  },
  purchase_order: {
    titleKey: 'messages.notification_sent.title_purchase_order',
    descriptionKey: 'messages.notification_sent.description_purchase_order',
  },
};

export function NotificationSentCard({
  msg,
}: Readonly<{ msg: NotificationSentMessage }>) {
  const { t } = useTranslation('messages');
  const { emailHtmlBody, recipient, status } = msg;

  const { titleKey, descriptionKey } = KIND_KEYS[msg.kind];
  const title = t(titleKey, { orderNumber: msg.orderNumber });
  const description = t(descriptionKey);

  return (
    <div
      data-testid="notification-sent-card"
      className="flex flex-col items-center gap-1"
    >
      <span className="text-xs text-neut-400">{msg.timestamp}</span>
      <div className="w-full max-w-md rounded-2xl p-4 border bg-neut-bg-alt border-neut-200">
        <div className="flex items-start gap-3">
          <Send size={18} className="text-neut-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p
              data-testid="notification-sent-title"
              className="text-sm font-semibold text-neut-prim"
            >
              {title}
            </p>
            <p className="text-xs text-neut-500 mt-2">{description}</p>
            <p
              data-testid="notification-sent-state"
              data-status={status}
              className={`text-xs font-medium mt-1 ${STATE_STYLES[status]}`}
            >
              {t(STATE_KEYS[status])}
            </p>
            {recipient && (
              <p
                data-testid="notification-sent-recipient"
                className="text-xs text-neut-600 mt-1 font-mono break-all"
              >
                {recipient}
              </p>
            )}
            {emailHtmlBody && (
              <div className="flex gap-2 mt-2.5">
                <button
                  type="button"
                  data-testid="notification-sent-preview"
                  onClick={() => openEmailPreview(emailHtmlBody)}
                  className="flex items-center gap-1 px-2.5 py-1 border border-neut-200 rounded-lg text-[10px] text-neut-500 hover:bg-neut-bg-alt transition-colors bg-neut-bg-alt"
                >
                  <Mail size={10} />
                  {t('messages.notification_sent.preview_email')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
